import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Divider,
  Typography,
  Chip,
  TextField,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { UserInfo } from '../UserSettingsDialog';

interface DeviceOption {
  id: string;
  name: string;
}

interface Props {
  userInfo: UserInfo;
  onUserInfoChange: (info: UserInfo) => void;
  onClose: () => void;
}

const ProfileTab: React.FC<Props> = ({ userInfo, onUserInfoChange, onClose }) => {
  const auth = useAuth();
  const { businesses } = useBusiness();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };

  const [defaultBusinessId, setDefaultBusinessId] = useState(userInfo.defaultBusinessId ?? '');
  const [defaultDeviceId, setDefaultDeviceId] = useState(userInfo.defaultDeviceId ?? '');
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Email confirmation
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Phone verification
  const [phoneNumber, setPhoneNumber] = useState(userInfo.phoneNumber ?? '');
  const [phoneCode, setPhoneCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneMessage, setPhoneMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!defaultBusinessId) {
      setDevices([]);
      return;
    }
    setLoadingDevices(true);
    fetch(`/api/devices?businessId=${defaultBusinessId}&topLevelOnly=true`, { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: DeviceOption[]) => setDevices(data))
      .finally(() => setLoadingDevices(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultBusinessId]);

  const handleBusinessChange = (id: string) => {
    setDefaultBusinessId(id);
    // A device default only makes sense within its business — clear it when
    // switching businesses rather than silently keeping a stale/mismatched one.
    setDefaultDeviceId('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defaultBusinessId: defaultBusinessId || null,
          defaultDeviceId: defaultDeviceId || null,
          themePreference: userInfo.themePreference,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to save settings');
      }
      const updated: UserInfo = await response.json();
      onUserInfoChange(updated);
      setSaveSuccess(true);
    } catch {
      setSaveError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmailConfirmation = async () => {
    setSendingEmail(true);
    setEmailError(null);
    setEmailMessage(null);
    try {
      const response = await fetch('/api/users/me/email/send-confirmation', {
        method: 'POST',
        headers: authHeader,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send confirmation email');
      }
      setEmailMessage(data.message || 'Confirmation email sent.');
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to send confirmation email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendPhoneCode = async () => {
    setSendingCode(true);
    setPhoneError(null);
    setPhoneMessage(null);
    try {
      const response = await fetch('/api/users/me/phone/send-code', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }
      // Sending a code resets PhoneNumberConfirmed server-side immediately.
      onUserInfoChange({ ...userInfo, phoneNumber, phoneNumberConfirmed: false });
      setCodeSent(true);
      setPhoneMessage(data.message || 'Verification code sent.');
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    setVerifyingCode(true);
    setPhoneError(null);
    try {
      const response = await fetch('/api/users/me/phone/verify-code', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: phoneCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Invalid or expired code');
      }
      onUserInfoChange(data as UserInfo);
      setCodeSent(false);
      setPhoneCode('');
      setPhoneMessage('Phone number verified.');
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Invalid or expired code');
    } finally {
      setVerifyingCode(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Verification status */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Account Verification
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ minWidth: 180 }}>
            Email — {userInfo.email}
          </Typography>
          {userInfo.emailConfirmed ? (
            <Chip icon={<CheckCircleIcon />} label="Confirmed" color="success" size="small" />
          ) : (
            <Button size="small" onClick={handleSendEmailConfirmation} disabled={sendingEmail}>
              {sendingEmail ? 'Sending…' : 'Send Confirmation Email'}
            </Button>
          )}
        </Box>
        {emailMessage && <Alert severity="success" sx={{ mb: 1 }}>{emailMessage}</Alert>}
        {emailError && <Alert severity="error" sx={{ mb: 1 }}>{emailError}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="04xx xxx xxx"
            sx={{ minWidth: 180 }}
          />
          {userInfo.phoneNumberConfirmed && phoneNumber === userInfo.phoneNumber ? (
            <Chip icon={<CheckCircleIcon />} label="Confirmed" color="success" size="small" />
          ) : (
            <Button
              size="small"
              onClick={handleSendPhoneCode}
              disabled={sendingCode || !phoneNumber}
            >
              {sendingCode ? 'Sending…' : codeSent ? 'Resend Code' : 'Send Code'}
            </Button>
          )}
        </Box>

        {codeSent && !userInfo.phoneNumberConfirmed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <TextField
              size="small"
              label="Verification Code"
              value={phoneCode}
              onChange={(e) => setPhoneCode(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleVerifyPhoneCode}
              disabled={verifyingCode || !phoneCode}
              startIcon={verifyingCode ? <CircularProgress size={16} /> : null}
            >
              Verify
            </Button>
          </Box>
        )}
        {phoneMessage && <Alert severity="success" sx={{ mt: 1 }}>{phoneMessage}</Alert>}
        {phoneError && <Alert severity="error" sx={{ mt: 1 }}>{phoneError}</Alert>}
      </Box>

      <Divider />

      {/* Defaults */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Defaults
        </Typography>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Settings saved.</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="default-business-label">Default Business</InputLabel>
            <Select
              labelId="default-business-label"
              value={defaultBusinessId}
              label="Default Business"
              onChange={(e) => handleBusinessChange(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {businesses.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!defaultBusinessId || loadingDevices}>
            <InputLabel id="default-device-label">Default Device</InputLabel>
            <Select
              labelId="default-device-label"
              value={defaultDeviceId}
              label="Default Device"
              onChange={(e) => setDefaultDeviceId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileTab;
